import CastForEducation from "@mui/icons-material/CastForEducation";
import IncompleteCircleIcon from "@mui/icons-material/IncompleteCircle";
import MenuBook from "@mui/icons-material/MenuBook";
import { Box, type SxProps } from "@mui/material";
import type { GridRowParams } from "@mui/x-data-grid";
import type { TFunction } from "i18next";
import type { DateTime } from "luxon";
import { type CSSProperties, type ReactNode, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { NURSE_TYPE_FIELD, REQUESTED_AT_FIELD, requestedAtDefaultVisibility, SENIORITY_FIELD } from "@/components/NursesMatrixGrid/columnVisibility";
import { buildDayColumns, getDayColumnField, highlightedColumnSx } from "@/components/NursesMatrixGrid/dayColumns";
import { useRequestUtils } from "@/components/RequestData/Provider/useRequestUtils";
import { useChosenDepartment } from "@/contexts/ChosenDepartment";
import { useDepartmentFlags } from "@/hooks/useDepartmentFlags";
import { useRevealWage } from "@/hooks/useRevealWage";
import { useRosterLookup } from "@/hooks/useRosterLookup";
import { useScheduleCacheEnabled } from "@/hooks/useScheduleWeekRules";
import { requestApprovalsMinimizedTableSortingKey } from "@/services/LocalStorageService";
import { NEUTRAL } from "@/theme/tokens";
import { OvertimeCell } from "@/ui/Cells/OvertimeCell/OvertimeCell";
import { shouldShowOtColumn } from "@/ui/Cells/OvertimeCell/otVisibility";
import { formatRequestedAt, RequestedAtCell } from "@/ui/Cells/RequestedAtCell/RequestedAtCell";
import { SeniorityCell } from "@/ui/Cells/SeniorityCell/SeniorityCell";
import ColorfulRole from "@/ui/ColorfulRole/ColorfulRole";
import { SystemDataGrid } from "@/ui/Tables/SystemDataGrid";
import type { ColumnPickerOptions, SystemGridColDef } from "@/ui/Tables/SystemDataGrid/types";
import { ThemedSvg } from "@/ui/ThemedSvg";
import { type NurseData, type NurseFullRole, NurseTrainingStatus, type OpenShiftRequest, type PanelShiftData, type ReplaceShiftRequest } from "@/utils/models";
import type { MultiSortOption, SortingState } from "@/utils/multiColumnSorting";
import { createDateRange, findDateIndex } from "@/utils/nursePanelUtils";
import { roleAverageLatestWagesOf, roleAverageWagesOf } from "@/utils/roleAverageWages";
import { DenyNurseButton, RestoreNurseButton } from "../DenyNurseButton";

type MinimizedNurseDataRow = {
	name: string;
	ot: number;
	weeklyOt: number;
	predictionCost: string;
	id: number;
	bonus?: number;
	shifts: PanelShiftData[];
	selectable?: boolean;
	accepted_start_time?: DateTime;
	accepted_end_time?: DateTime;
	isDenied?: boolean;
	nurseStartDate?: DateTime;
	nurseType?: string;
	requestedAt?: DateTime | null;
};

// Fixed per-column widths (px). The popup is a fixed width (see MultiSizePopup): the name column flexes to
// fill it, the rest keep these widths, and the grid scrolls horizontally when their total overflows.
const NAME_COL_WIDTH = 140;
const OT_COL_WIDTH = 60;
const PREDICTION_COST_COL_WIDTH = 160;
const SENIORITY_COL_WIDTH = 90;
const NURSE_TYPE_COL_WIDTH = 100;
// 170 matches the full grid: a whole DATETIME_MED value ellipsizes below this.
const REQUESTED_AT_COL_WIDTH = 170;
const DENY_COL_WIDTH = 48;

/** Synthetic-picker id for the single "Days" toggle (shows/hides all day columns as one block). */
const DAYS_TOGGLE_ID = "days";

/** Grid testid root — also the prefix for the per-row cell testids built off it. */
const GRID_TEST_ID = "minimized-nurse-data-table";

// Selected row tint + denied-row (strikethrough, non-interactive) styling. Mirrors the full nurses matrix
// grid, including the shared highlighted-day-column rails.
const gridSx = {
	"& .MuiDataGrid-row": { cursor: "pointer" },
	"& .MuiDataGrid-row.selected-row": { backgroundColor: NEUTRAL[200] },
	"& .MuiDataGrid-row.denied-row": { cursor: "not-allowed", opacity: 0.5, "& .MuiDataGrid-cell": { textDecoration: "line-through" } },
	...highlightedColumnSx,
} as const satisfies SxProps;

type Props = {
	nurseData: NurseData[];
	title?: ReactNode;
	onRowClick?: (nurseId: number) => void;
	selectedNurseId?: number;
	maxTableHeight?: CSSProperties["maxHeight"];
	request: OpenShiftRequest | ReplaceShiftRequest;
	/** When set, renders a trailing trash-icon cell on each row that calls this with the nurse id. */
	onDenyNurse?: (nurseId: number) => void;
	/** When set, queued-deny rows render an un-deny icon that calls this with the nurse id. */
	onRestoreNurse?: (nurseId: number) => void;
	/** Nurses queued for deny this session — rendered with strikethrough and an un-deny icon. */
	queuedDenyNurseIds?: number[];
	/**
	 * Swaps the grid BODY for skeletons while a fetch is in flight. Callers must pass this rather than
	 * rendering their own skeletons in place of the grid: unmounting the grid on every background refetch
	 * throws away its column-visibility, sort, and search state (and closes an open column picker).
	 */
	loading?: boolean;
};

/**
 * Compact nurse picker for the minimized request-approval popup, built on the shared `SystemDataGrid`
 * (name / OT / predicted-cost + the per-day shift columns + optional deny). The day columns reuse the
 * `NursesMatrixGrid` builder; it inherits the grid's sort popup and native column picker (OT, Predicted
 * Cost, and the "Days" block are toggleable; name/deny are fixed). Since the popup is a fixed width, the
 * grid scrolls horizontally when the day columns overflow. OT is dropped entirely for medical departments.
 */
export const MinimizedNurseDataTable = ({
	nurseData,
	title,
	onRowClick,
	selectedNurseId,
	maxTableHeight,
	request,
	onDenyNurse,
	onRestoreNurse,
	queuedDenyNurseIds,
	loading,
}: Props) => {
	const { t } = useTranslation();
	const { isPartialShift } = useRequestUtils();
	const hideWage = !useRevealWage();
	const { chosenDepartment } = useChosenDepartment();
	const { isMedicalDepartment } = useDepartmentFlags();
	const { roster, getNurseById, getNurseFormattedName } = useRosterLookup(chosenDepartment.department_id);
	const isCacheEnabled = useScheduleCacheEnabled();
	// DEFAULT: no averages until the roster loads; a nurse without a wage then shows no estimate, as with an empty roster.
	// With the cache on from their latest wages, which the staff list keeps without the wage history.
	const roleAverageWage = useMemo(() => (isCacheEnabled ? roleAverageLatestWagesOf : roleAverageWagesOf)(roster ?? []), [isCacheEnabled, roster]);

	const rows = useMemo(() => nurseData.map((nurse) => mapNurseDataToRow(nurse, roleAverageWage, hideWage, t)), [nurseData, roleAverageWage, hideWage, t]);
	const queuedDenyIdSet = useMemo(() => new Set(queuedDenyNurseIds ?? []), [queuedDenyNurseIds]);
	// Contract Type is only built when the rows actually carry one. Scanned across ALL rows (like the full
	// grid's job column) rather than sampling row 0 — a lead row whose nurse is missing from hospitalData
	// carries no `nurseType`, and sampling it would drop the column for everyone.
	const hasNurseType = nurseData.some((nurse) => nurse.nurseType != null);
	// Same data gate as the full grid: only the approval feed sends a submission time, and a declined nurse
	// carries none — so scan ALL rows rather than sampling row 0, which can be the declined one.
	const hasRequestedAt = nurseData.some((nurse) => nurse.requestedAt != null);

	// Day columns mirror the full grid: same Sunday-based week (`createDateRange`) and highlighted request day
	// (`findDateIndex`), so `row.shifts[dayIndex]` lines up exactly with the header dates.
	const requestDate = request.message.shift_start;
	const dateRange = useMemo(() => createDateRange(requestDate), [requestDate]);
	const highlightDateIndexes = useMemo(() => [findDateIndex(dateRange, requestDate)], [dateRange, requestDate]);
	const getIsPartial = useCallback((nurseId: number) => isPartialShift(request, nurseId), [isPartialShift, request]);

	const columns = useMemo<SystemGridColDef<MinimizedNurseDataRow>[]>(() => {
		const cols: SystemGridColDef<MinimizedNurseDataRow>[] = [
			{
				field: "name",
				headerName: t("name"),
				width: NAME_COL_WIDTH,
				excludeFromColumnPicker: true,
				renderCell: ({ row }) => {
					const nurse = getNurseById(row.id);
					const isTrainee = nurse?.trainingStatus === NurseTrainingStatus.Trainee;
					const isCharge = nurse?.isShiftManager || nurse?.canBeReliefCharge || false;
					const isOrientation = nurse?.trainingStatus === NurseTrainingStatus.Orientation;
					const isEducation = nurse?.trainingStatus === NurseTrainingStatus.Education;
					const isRequestPartialShift = isPartialShift(request, row.id);
					const partialShiftTooltipText = `${row.accepted_start_time?.toFormat("h:mma")} - ${row.accepted_end_time?.toFormat("h:mma")}`;
					return (
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
							<Box title={row.name} sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0, overflow: "hidden" }}>
								{nurse?.role && <ColorfulRole role={nurse.role} />}
								<Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
									{getNurseFormattedName(row.id)}
								</Box>
							</Box>
							<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
								{isCharge && <ThemedSvg src="/charge.svg" alt={t("charge")} size={16} color="var(--inhouse-primary)" />}
								{isTrainee && <ThemedSvg src="/student.svg" alt={t("trainee")} size={12} color="var(--inhouse-primary)" />}
								{isOrientation && <MenuBook sx={{ width: "12px", height: "12px" }} />}
								{isEducation && <CastForEducation sx={{ width: "12px", height: "12px" }} />}
								{isRequestPartialShift && (
									<InHouseTooltip title={partialShiftTooltipText}>
										<IncompleteCircleIcon sx={{ width: "12px", height: "12px" }} />
									</InHouseTooltip>
								)}
							</Box>
						</Box>
					);
				},
			},
		];

		// OT is not shown for medical departments (matches the desktop grid + mobile cards). Uses the shared
		// OvertimeCell so OT renders identically everywhere (isAdd=true — the popup is always an add flow).
		if (shouldShowOtColumn({ hasOvertimeData: true, isMedicalDepartment })) {
			cols.push({
				field: "ot",
				headerName: t("nurses.matrix.ot"),
				width: OT_COL_WIDTH,
				align: "center",
				display: "flex",
				headerAlign: "center",
				renderCell: ({ row }) => <OvertimeCell overtimeHours={row.ot} weeklyOvertimeHoursBeforeChange={row.weeklyOt} showWeeklyOt isAdd t={t} />,
			});
		}

		cols.push({
			field: "predictionCost",
			headerName: t("predicted.cost"),
			width: PREDICTION_COST_COL_WIDTH,
			renderCell: ({ row }) => (
				<>
					{row.predictionCost} {row.bonus ? `(+$${row.bonus})` : null}
				</>
			),
		});

		cols.push({
			field: SENIORITY_FIELD,
			headerName: t("nurses.matrix.seniority"),
			width: SENIORITY_COL_WIDTH,
			align: "center",
			display: "flex",
			headerAlign: "center",
			renderCell: ({ row }) => <SeniorityCell nurseStartDate={row.nurseStartDate} t={t} />,
		});

		if (hasNurseType) {
			cols.push({
				field: NURSE_TYPE_FIELD,
				headerName: t("nurses.matrix.nurse.type"),
				width: NURSE_TYPE_COL_WIDTH,
				align: "center",
				display: "flex",
				headerAlign: "center",
			});
		}

		if (hasRequestedAt) {
			cols.push({
				field: REQUESTED_AT_FIELD,
				headerName: t("nurses.matrix.requested.at"),
				width: REQUESTED_AT_COL_WIDTH,
				align: "center",
				display: "flex",
				headerAlign: "center",
				// valueFormatter feeds clipboard copy / export; renderCell owns the display and tooltip.
				valueFormatter: (value: DateTime | null | undefined) => formatRequestedAt(value),
				renderCell: ({ row }) => <RequestedAtCell requestedAt={row.requestedAt} cellTestId={`${GRID_TEST_ID}-requested-at-${row.id}`} />,
			});
		}

		buildDayColumns<MinimizedNurseDataRow>({ dateRange, highlightDateIndexes, hightlightOriginalShift: false, getIsPartial, darkShiftIcons: false, t }, cols);

		if (onDenyNurse) {
			cols.push({
				field: "__deny",
				headerName: "",
				width: DENY_COL_WIDTH,
				sortable: false,
				disableColumnMenu: true,
				disableExport: true,
				excludeFromColumnPicker: true,
				align: "center",
				headerAlign: "center",
				renderCell: ({ row }) => {
					const isServerDenied = row.isDenied === true;
					const isQueuedForDeny = queuedDenyIdSet.has(row.id);
					if (isServerDenied) return null;
					return isQueuedForDeny ? (
						onRestoreNurse && <RestoreNurseButton nurseId={row.id} onRestore={onRestoreNurse} iconFontSize={18} />
					) : (
						<DenyNurseButton nurseId={row.id} onDeny={onDenyNurse} iconFontSize={18} />
					);
				},
			});
		}

		return cols;
	}, [
		isMedicalDepartment,
		hasNurseType,
		hasRequestedAt,
		onDenyNurse,
		onRestoreNurse,
		queuedDenyIdSet,
		request,
		isPartialShift,
		getNurseById,
		getNurseFormattedName,
		dateRange,
		highlightDateIndexes,
		getIsPartial,
		t,
	]);

	const sortingOptions = useMemo<Array<MultiSortOption<MinimizedNurseDataRow>>>(() => {
		const options: Array<MultiSortOption<MinimizedNurseDataRow>> = [
			{ sortColumns: ["name"], label: t("name") },
			{ sortColumns: ["ot"], label: t("nurses.matrix.ot") },
			{ sortColumns: ["predictionCost"], label: t("predicted.cost") },
			{ sortColumns: [SENIORITY_FIELD], label: t("nurses.matrix.seniority") },
		];
		if (hasNurseType) options.push({ sortColumns: [NURSE_TYPE_FIELD], label: t("nurses.matrix.nurse.type") });
		// Keeps declined nurses (no submission time) last in BOTH directions. Scoped to this entry only —
		// `useMultiSort` matches the flag per sort axis, so it can't spill onto Name/OT/Seniority.
		if (hasRequestedAt) options.push({ sortColumns: [REQUESTED_AT_FIELD], label: t("nurses.matrix.requested.at"), sortNullUndefinedLast: true });
		// Medical departments never surface OT, so drop its sort option too (the column isn't rendered).
		return isMedicalDepartment ? options.filter((o) => o.sortColumns?.[0] !== "ot") : options;
	}, [isMedicalDepartment, hasNurseType, hasRequestedAt, t]);

	const defaultSortState = useMemo<Array<SortingState<MinimizedNurseDataRow>>>(
		() => [
			{ sortColumns: ["name"], sortOrder: "asc", label: t("name") },
			{ sortColumns: ["id"], sortOrder: "asc" },
		],
		[t],
	);

	const handleRowClick = useCallback(
		(params: GridRowParams<MinimizedNurseDataRow>) => {
			const isDenied = queuedDenyIdSet.has(params.row.id) || params.row.isDenied === true;
			if (isDenied) return;
			onRowClick?.(params.row.id);
		},
		[onRowClick, queuedDenyIdSet],
	);

	const getRowClassName = useCallback(
		(params: GridRowParams<MinimizedNurseDataRow>) => {
			const isDenied = queuedDenyIdSet.has(params.row.id) || params.row.isDenied === true;
			const classes: string[] = [];
			if (params.row.id === selectedNurseId) classes.push("selected-row");
			if (isDenied) classes.push("denied-row");
			return classes.join(" ");
		},
		[selectedNurseId, queuedDenyIdSet],
	);

	// SystemDataGrid's containedHeight bounds the grid to a numeric px max, then scrolls internally. The only
	// caller passes "200px"; fall back to the grid's default cap when the value isn't a parseable pixel number.
	const containedHeight = useMemo(() => {
		const parsed = typeof maxTableHeight === "number" ? maxTableHeight : Number.parseInt(String(maxTableHeight ?? ""), 10);
		return Number.isFinite(parsed) ? { maxHeight: parsed } : true;
	}, [maxTableHeight]);

	const dataGridProps = useMemo(
		() => ({
			hideFooter: true,
			// Render every row on one virtualized page (no pagination footer) — the popup is short and scrolls.
			pageSizeOptions: [{ value: -1, label: t("All") }],
			initialState: { pagination: { paginationModel: { pageSize: -1 } } },
			onRowClick: handleRowClick,
			getRowClassName,
		}),
		[handleRowClick, getRowClassName, t],
	);

	const columnPicker = useMemo<ColumnPickerOptions>(() => {
		const dayFields = dateRange.map((_, index) => getDayColumnField(index));
		return {
			syntheticColumns: [{ id: DAYS_TOGGLE_ID, label: t("nurses.matrix.days"), controlledFields: dayFields }],
			defaultVisibility: {
				...Object.fromEntries(dayFields.map((field) => [field, false])),
				[SENIORITY_FIELD]: false,
				[NURSE_TYPE_FIELD]: false,
				...requestedAtDefaultVisibility,
			},
			onlyShowIcon: true,
		};
	}, [dateRange, t]);

	return (
		<Box sx={{ width: "100%" }}>
			<SystemDataGrid<MinimizedNurseDataRow>
				rows={rows}
				columns={columns}
				getRowId={(row) => row.id}
				sortingOptions={sortingOptions}
				defaultSortState={defaultSortState}
				localStorageKey={requestApprovalsMinimizedTableSortingKey}
				columnPicker={columnPicker}
				loading={loading}
				leadingToolbarNodes={title}
				containedHeight={containedHeight}
				dataGridProps={dataGridProps}
				sx={gridSx}
				dataTestId={GRID_TEST_ID}
			/>
		</Box>
	);
};

/**
 * Reconstructs the flat grid row from a `NurseData`. Exported for tests: every field the minimized
 * approval shows has to be listed here explicitly, so a field added to `NurseData` and to the full grid
 * silently vanishes in minimized mode unless it is also copied across — which is exactly how the
 * Requested column went missing here.
 */
export function mapNurseDataToRow(
	nurse: NurseData,
	roleAverageWage: Partial<Record<NurseFullRole, string>>,
	hideWage: boolean,
	t: TFunction,
): MinimizedNurseDataRow {
	let shiftDurationHours: number = 1;

	if (nurse.shiftTimes) {
		const { start, end } = nurse.shiftTimes;
		shiftDurationHours = end.diff(start, "hours").as("hours");
	}
	const est = Number(roleAverageWage[nurse.role as NurseFullRole]) * shiftDurationHours;

	return {
		name: nurse.name,
		ot: nurse.overtimeHours ?? 0,
		weeklyOt: nurse.weeklyOvertimeHoursBeforeChange ?? 0,
		predictionCost: hideWage
			? t("hidden")
			: nurse.predictedCost && nurse.predictedCost !== "$0.0"
				? nurse.predictedCost
				: Number.isNaN(est)
					? t("hidden")
					: `est. $${est.toFixed(2)}`,
		id: nurse.id,
		bonus: nurse.bonus ?? 0,
		shifts: nurse.shifts,
		selectable: nurse.selectable,
		accepted_start_time: nurse.accepted_start_time,
		accepted_end_time: nurse.accepted_end_time,
		isDenied: nurse.isDenied,
		nurseStartDate: nurse.nurseStartDate,
		nurseType: nurse.nurseType,
		requestedAt: nurse.requestedAt,
	};
}
